import React, { useState, useEffect } from 'react';
import { Transition, animated } from 'react-spring/renderprops';
import { ClickOutsideDetect } from '@montana/shared/click-outside-detect';
import { uuidv4 } from "@montana/helpers/guid-helper";
import { onPopup, setGlobalPopupId } from "./popup.helper";
import { Link } from '../link';

interface IPopupProps {
    id?: string;
    animate?: boolean;
    timeout?: number;
    content: JSX.Element | JSX.Element[];
    children: JSX.Element | JSX.Element[];
    className?: string;
    onClick?: (event?: React.MouseEvent) => void;
    href?: string;
    disabled?: boolean;
}

export const Popup: React.FC<IPopupProps> = React.memo(({ id, animate, timeout, className, content, children, onClick, href, disabled }: IPopupProps): JSX.Element => {
    const autoCloseTimeout = typeof timeout === 'number' ? timeout : 5000;

    const [popupID] = useState(id || uuidv4());
    const [isAnimated, setIsAnimated] = useState(typeof animate === 'boolean' ? animate : true);
    const [showPopup, setShowPopup] = useState(false);
    const [inFocus, setInFocus] = useState(false);
    const [popupActive, setPopupActive] = useState(false);
    const [popupTimeout, setPopupTimeout] = useState<any>();

    const togglePopup = (state?: boolean): void => {
        const newState = typeof state === 'boolean' ? state : !showPopup;
        setShowPopup(newState);
        if (!newState) {
            setInFocus(false);
        }
    };

    const onToggleButtonClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
        if (onClick) {
            onClick(event);
        }
    };

    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (!inFocus) {
            timeout = setTimeout(() => {
                setShowPopup(false);
            }, 2000) as unknown as NodeJS.Timeout;
        }

        return () => {
            clearTimeout(timeout);
        }
    }, [inFocus])

    onPopup((id) => {
        togglePopup(id === popupID);
    });

    useEffect(() => {
        setIsAnimated(typeof animate === 'boolean' ? animate : true);
    }, [animate]);

    useEffect(() => {
        clearTimeout(popupTimeout);
        if (showPopup) {
            setPopupTimeout(setTimeout(() => {
                togglePopup(false);
            }, autoCloseTimeout));
        }
    }, [showPopup]);

    useEffect(() => {
        const newState = (showPopup || inFocus) && disabled !== true;
        setPopupActive(newState);
        if (newState) {
            setGlobalPopupId(popupID);
        }
    }, [showPopup, inFocus, disabled]);

    return (
        <ClickOutsideDetect onClickOutside={() => togglePopup(false)} onFocusOutside={() => setInFocus(false)} onFocusInside={() => setInFocus(true)}>
            <div
                className={[className, 'popup', !isAnimated ? 'popup--no-animation' : ''].join(' ')}
                onMouseEnter={() => setInFocus(true)}
                onMouseLeave={() => setInFocus(false)}>
                {(
                    href
                        ? <Link to={href} className={['popup__toggle', disabled ? 'popup__toggle--disabled' : ''].join(' ')}>
                            {children}
                        </Link>
                        : <button name="" onClick={onToggleButtonClick} className="popup__toggle">
                            {children}
                        </button>
                )}
                <div className="popup__modal">
                    <Transition
                        items={popupActive}
                        config={{ tension: (popupActive) ? 350 : 500, friction: 25, clamp: true }}
                        unique={true}
                        from={{ height: 0, opacity: 1 }}
                        enter={{ height: 'auto', opacity: 1 }}
                        leave={{ height: 'auto', opacity: 0 }}
                        immediate={!isAnimated}>
                        {(item) => styles => (
                            item && <animated.div style={{ ...{ overflow: 'hidden' }, ...styles }} className="popup__box">
                                <div className="popup__content" onPointerEnter={() => setInFocus(true)}>
                                    {content}
                                </div>
                            </animated.div>
                        )}
                    </Transition>
                </div>
            </div>
        </ClickOutsideDetect >
    );
});
